import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BreadCrumb from "../Controls/BreadCrumb";
import CoachTodoList from "./CoachTodoList";
import MatchStatusPill from "../Controls/MatchStatusPill";
import API from "@src/API";
import "../../style/scss/Recruiter.scss";

const CONSTRAINT_LABELS = {
  compensation: "Pay below what candidates need",
  compensation_floor: "Pay below what candidates need",
  compensation_below_min: "Pay below what candidates need",
  location: "Location requirements",
  skills: "Must-have skills",
  must_have_skills: "Must-have skills",
  employment_type: "Employment type",
  travel: "Travel expectations",
  onsite: "On-site expectations",
  wfh: "Remote expectations",
};

function pick(obj, name) {
  if (!obj) return undefined;
  return obj[name] ?? obj[name.charAt(0).toLowerCase() + name.slice(1)];
}

export default function JobWorkspace() {
  const { id } = useParams();
  const jobId = Number(id);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!jobId) return;
    try {
      const resp = await API.getAsync(`/Job/Workspace?jobId=${jobId}`);
      if (resp?.ok) {
        setData(resp.jsonData);
        setError("");
      } else if (resp?.status === 403) {
        setError("You do not have access to this opening.");
      } else {
        setError("Could not load this opening.");
      }
    } catch {
      setError("Could not load this opening.");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const status = pick(data, "MatchStatus");

  // While a run is in flight the counts below are last-known, so keep polling.
  useEffect(() => {
    if (status !== "Calculating") {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return undefined;
    }
    pollRef.current = setInterval(refresh, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [status, refresh]);

  async function rerun() {
    setBusy("rerun");
    try {
      await API.postAsync(`/FindCandidates?jobId=${jobId}`, {});
      await refresh();
    } finally {
      setBusy("");
    }
  }

  async function setStatus(jobCandidateId, next) {
    setBusy(`status-${jobCandidateId}`);
    try {
      await API.postAsync("/JobCandidate/Status", { JobCandidateID: jobCandidateId, Status: next });
      await refresh();
    } finally {
      setBusy("");
    }
  }

  async function notifyShortlist() {
    setBusy("notify");
    try {
      await API.postAsync("/JobCandidates/Notify", { JobID: jobId });
      await refresh();
    } finally {
      setBusy("");
    }
  }

  const candidates = pick(data, "Candidates") || [];
  const constraints = pick(data, "Constraints") || {};
  const constraintRows = Object.entries(constraints)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);
  const worst = constraintRows.length > 0 ? constraintRows[0][1] : 0;
  const employerId = pick(data, "EmployerID");
  const shortlisted = candidates.filter((c) => (pick(c, "StatusName") || "") === "Shortlisted").length;

  return (
    <div className="pagebody recruiter-dashboard job-workspace">
      <BreadCrumb
        props={[
          { label: "Hiring", route: "/recruiter" },
          { label: pick(data, "EmployerName") || "Company", route: `/company/${employerId || ""}` },
          { label: pick(data, "Title") || "Opening", route: `/jobWorkspace/${jobId}` },
        ]}
      />
      <div className="tabbody">
        <div className="recruiter-dashboard__hero">
          <div>
            <h2>{pick(data, "Title") || "Opening"}</h2>
            <p className="byline">
              <MatchStatusPill status={status} asOfUtc={pick(data, "LastCompletedUtc")} />{" "}
              {status === "Calculating"
                ? "Counts below are from the previous run until this one finishes."
                : null}
            </p>
          </div>
          <div className="recruiter-dashboard__stats">
            <div>
              <span className="stat">{pick(data, "MatchCount") ?? 0}</span>
              <span className="statLabel">matched</span>
            </div>
            <div>
              <span className="stat">{pick(data, "InProcessCount") ?? 0}</span>
              <span className="statLabel">in process</span>
            </div>
          </div>
        </div>

        {error ? <p className="recruiter-dashboard__error">{error}</p> : null}
        {loading && !data ? <p className="byline">Loading the opening…</p> : null}

        <div className="job-workspace__actions">
          <button type="button" onClick={rerun} disabled={busy === "rerun" || status === "Calculating"}>
            {busy === "rerun" ? "Re-running…" : "Re-run matching"}
          </button>
          <button type="button" onClick={notifyShortlist} disabled={busy === "notify" || shortlisted === 0}>
            {busy === "notify" ? "Notifying…" : `Notify shortlist (${shortlisted})`}
          </button>
          <Link to={`/jobMatches/${jobId}`}>Full match review</Link>
        </div>

        <CoachTodoList todos={pick(data, "Todos") || []} title="What will fill this role fastest" />

        <div className="tabsection">
          <div className="listTitle">
            <span>What eliminated candidates</span>
          </div>
          {constraintRows.length === 0 ? (
            <p className="byline">
              No elimination diagnostics yet. They appear after the next match run.
            </p>
          ) : (
            <ul className="constraint-bars">
              {constraintRows.map(([key, count]) => (
                <li key={key}>
                  <span className="constraint-bars__label">{CONSTRAINT_LABELS[key] || key}</span>
                  <span className="constraint-bars__bar">
                    <span style={{ width: `${worst ? Math.round((count / worst) * 100) : 0}%` }} />
                  </span>
                  <span className="constraint-bars__count">{count}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="byline">
            {pick(data, "CandidatesConsidered") ?? 0} people were considered,{" "}
            {pick(data, "CandidatesEliminated") ?? 0} were eliminated by your criteria.
          </p>
        </div>

        <div className="tabsection">
          <div className="listTitle">
            <span>Candidates</span>
          </div>
          {candidates.length === 0 ? (
            <p className="byline">
              No candidates yet. Relaxing the top criterion above is usually what changes that.
            </p>
          ) : (
            <ul className="candidate-list">
              {candidates.map((c) => {
                const cid = pick(c, "JobCandidateID");
                const statusName = pick(c, "StatusName");
                return (
                  <li key={cid}>
                    <div className="candidate-list__head">
                      <strong>{pick(c, "Name")}</strong>
                      <span className="candidate-list__score">{pick(c, "Score")}</span>
                    </div>
                    <div className="byline">
                      {statusName}
                      {pick(c, "InProcess") ? " · in process" : ""}
                      {pick(c, "Notified") ? " · notified" : ""}
                      {pick(c, "OpeningInterest") ? ` · ${pick(c, "OpeningInterest")}` : ""}
                    </div>
                    <div className="candidate-list__actions">
                      <button
                        type="button"
                        disabled={busy === `status-${cid}` || statusName === "Shortlisted"}
                        onClick={() => setStatus(cid, 4)}
                      >
                        Shortlist
                      </button>
                      <button
                        type="button"
                        disabled={busy === `status-${cid}` || statusName === "Declined"}
                        onClick={() => setStatus(cid, 3)}
                      >
                        Decline
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
